import React, { useRef, useState, useEffect } from "react";
import {
  View,
  TextInput,
  Pressable,
  StyleSheet,
  ScrollView,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";
import Avatar from "@/components/Avatar";
import { useTheme } from "@/hooks/useTheme";
import useContextMenu from "@/hooks/useContextMenu";

// ── Types ────────────────────────────────────────────────────

type IconName = keyof typeof MaterialCommunityIcons.glyphMap;

export type LookupInputState = "loading" | "idle" | "editing" | "saving";

export type LookupInputOption = {
  id: string;
  name: string;
  subtitle?: string;
  highlightSub?: boolean;
  icon?: IconName;
  avatar?: { firstName?: string; lastName?: string; email?: string; userId?: string };
  badge?: { text: string; tone: "sky" | "emerald" | "amber" | "red" | "violet" | "slate" };
};

type LookupInputProps = {
  label: string;
  value: string | null;
  options: LookupInputOption[];
  state: LookupInputState;
  icon?: IconName;
  error?: string;
  info?: string;
  placeholder?: string;
  onChange?: (optionId: string | null) => void;
  /** Called when the value is tapped in idle state (e.g. to navigate to entity) */
  onIdlePress?: () => void;
  /** Optional action button shown in idle state (e.g. phone call) */
  idleAction?: { icon: IconName; onPress: () => void };
};

// ── Badge tone → color helper ────────────────────────────────

function useBadgeColor(tone: string) {
  const { colors } = useTheme();
  const map: Record<string, { bg: string; text: string }> = {
    sky: { bg: colors.sky.background, text: colors.sky.text },
    emerald: { bg: colors.emerald.background, text: colors.emerald.text },
    amber: { bg: colors.amber.background, text: colors.amber.text },
    red: { bg: colors.red.background, text: colors.red.text },
    violet: { bg: colors.violet.background, text: colors.violet.text },
    slate: { bg: colors.slate.background, text: colors.slate.text },
  };
  return map[tone] ?? map.slate;
}

function BadgePill({ text, tone }: { text: string; tone: string }) {
  const c = useBadgeColor(tone);
  return (
    <View style={[styles.badge, { backgroundColor: c.bg }]}>
      <ThemedText style={[styles.badgeText, { color: c.text }]}>{text}</ThemedText>
    </View>
  );
}

// ── Component ────────────────────────────────────────────────

export function LookupInput({
  label,
  value,
  options,
  icon = "magnify",
  state,
  error,
  info,
  placeholder = "Select\u2026",
  onChange,
  onIdlePress,
  idleAction,
}: LookupInputProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const selectorRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const isLoading = state === "loading";
  const isEditing = state === "editing" || state === "saving";
  const isSaving = state === "saving";

  const selected = options.find((o) => o.id === value) ?? null;

  const handleOpen = () => {
    if (!isEditing || isSaving) return;

    contextMenu.setIgnoreRef(selectorRef);
    selectorRef.current?.measureInWindow((x, y, w, h) => {
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 2 });
      contextMenu.setStyle({
        backgroundColor: colors.card,
        overflow: "hidden",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
      });
      contextMenu.setMenuContent(
        <View style={{ minWidth: w }}>
          {(() => {
            const lower = query.toLowerCase();
            const filtered = query
              ? options.filter((o) => o.name.toLowerCase().includes(lower) || o.subtitle?.toLowerCase().includes(lower))
              : options;
            return filtered.length === 0 ? (
              <View style={styles.emptyRow}>
                <MaterialCommunityIcons name="magnify-close" size={16} color={colors.textSecondary} />
                <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                  No results for &ldquo;{query}&rdquo;
                </ThemedText>
              </View>
            ) : (
              <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled">
                {filtered.map((opt) => {
                  const isSelected = opt.id === value;
                  return (
                    <Pressable
                      key={opt.id}
                      style={({ hovered }) => [
                        styles.optionRow,
                        {
                          backgroundColor: isSelected
                            ? colors.primary + "15"
                            : hovered
                              ? colors.backgroundSecondary
                              : "transparent",
                        },
                      ]}
                      onPress={() => {
                        console.log("[LookupInput] onPick", opt.id, opt.name);
                        onChange?.(opt.id);
                        contextMenu.setVisible(false);
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      {opt.avatar ? (
                        <Avatar
                          firstName={opt.avatar.firstName}
                          lastName={opt.avatar.lastName}
                          email={opt.avatar.email}
                          userId={opt.avatar.userId}
                          size={28}
                        />
                      ) : (
                        <MaterialCommunityIcons
                          name={opt.icon ?? icon}
                          size={18}
                          color={isSelected ? colors.primary : colors.textSecondary}
                        />
                      )}
                      <View style={styles.optionContent}>
                        <ThemedText
                          style={[
                            styles.optionName,
                            { color: isSelected ? colors.primary : colors.text },
                            isSelected ? { fontWeight: "600" } : undefined,
                          ]}
                        >
                          {opt.name}
                        </ThemedText>
                        {opt.subtitle ? (
                          <ThemedText style={[styles.optionSubtitle, { color: colors.textSecondary }]}>
                            {opt.subtitle}
                          </ThemedText>
                        ) : null}
                      </View>
                      {opt.badge && <BadgePill text={opt.badge.text} tone={opt.badge.tone} />}
                      {isSelected && <MaterialCommunityIcons name="check" size={16} color={colors.primary} />}
                    </Pressable>
                  );
                })}
              </ScrollView>
            );
          })()}
        </View>,
      );
      contextMenu.setVisible(true);
    });
  };

  useEffect(() => {
    if (open) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const key = e.nativeEvent.key;
    const nativeEvent = e.nativeEvent as unknown as KeyboardEvent;
    if (key === "Enter" && !nativeEvent.shiftKey) {
      e.preventDefault();
      const lower = query.toLowerCase();
      const filtered = query
        ? options.filter((o) => o.name.toLowerCase().includes(lower) || o.subtitle?.toLowerCase().includes(lower))
        : options;
      if (filtered.length > 0) {
        onChange?.(filtered[0].id);
        contextMenu.setVisible(false);
        setOpen(false);
        setQuery("");
      }
    }
  };

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <View style={styles.field}>
        <Skeleton width={60} height={10} />
        <Skeleton width="100%" height={40} radius={8} />
      </View>
    );
  }

  const hasError = Boolean(error);

  return (
    <View style={[styles.field, { opacity: isSaving ? 0.5 : 1 }]}>
      {/* Label row */}
      <View style={styles.labelRow}>
        <ThemedText style={[styles.label, { color: colors.textSecondary }]}>{label}</ThemedText>
        {info && (
          <View style={[styles.infoTag, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
            <MaterialCommunityIcons name="information-outline" size={11} color={colors.textSecondary} />
            <ThemedText style={[styles.infoText, { color: colors.textSecondary }]}>{info}</ThemedText>
          </View>
        )}
      </View>

      {/* Value / Input area */}
      {open ? (
        <View
          ref={selectorRef}
          style={[
            styles.inputRow,
            {
              backgroundColor: colors.backgroundSecondary,
              borderColor: hasError ? colors.red.text : colors.primary,
              borderWidth: 1.5,
            },
          ]}
        >
          <MaterialCommunityIcons name={icon} size={16} color={colors.textSecondary} />
          <TextInput
            ref={inputRef}
            value={query}
            onChangeText={setQuery}
            onKeyPress={handleKeyPress}
            onFocus={() => {
              console.log("[LookupInput] handleFocus");
              setTimeout(handleOpen, 50);
            }}
            onBlur={() => {
              setTimeout(() => {
                setOpen(false);
                setQuery("");
                contextMenu.setVisible(false);
              }, 150);
            }}
            placeholder={`Search ${label.toLowerCase()}\u2026`}
            placeholderTextColor={colors.placeholder}
            style={[styles.input, { color: colors.text }]}
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus
          />
          {query.length > 0 && (
            <Pressable
              onPress={() => {
                setQuery("");
              }}
              hitSlop={8}
              style={{ padding: 2 }}
            >
              <MaterialCommunityIcons
                name="close-circle"
                size={16}
                color={colors.textSecondary}
                style={{ opacity: 0.6 }}
              />
            </Pressable>
          )}
          <Pressable
            onPress={() => {
              setOpen(false);
              setQuery("");
            }}
            hitSlop={8}
            style={{ padding: 2 }}
          >
            <MaterialCommunityIcons name="chevron-up" size={18} color={colors.textSecondary} />
          </Pressable>
        </View>
      ) : (
        <Pressable
          ref={selectorRef}
          onPress={() => {
            if (isEditing && !isSaving) setOpen(true);
          }}
          style={({ hovered }) => [
            styles.valueRow,
            {
              backgroundColor: colors.backgroundSecondary,
              borderColor: hasError ? colors.red.text : colors.border,
              borderWidth: hasError ? 1 : StyleSheet.hairlineWidth,
            },
            isEditing && !isSaving && hovered && { borderColor: colors.primary },
          ]}
          disabled={!isEditing || isSaving}
        >
          {selected ? (
            <>
              {selected.avatar ? (
                <Avatar
                  firstName={selected.avatar.firstName}
                  lastName={selected.avatar.lastName}
                  email={selected.avatar.email}
                  userId={selected.avatar.userId}
                  size={28}
                />
              ) : (
                <MaterialCommunityIcons name={selected.icon ?? icon} size={18} color={colors.primary} />
              )}
              <View style={styles.valueContent}>
                {!isEditing && onIdlePress ? (
                  <Pressable onPress={onIdlePress} style={({ hovered }) => [hovered && { opacity: 0.7 }]}>
                    <ThemedText style={[styles.valueName, { color: colors.primary }]}>{selected.name}</ThemedText>
                  </Pressable>
                ) : (
                  <ThemedText style={styles.valueName}>{selected.name}</ThemedText>
                )}
                {selected.subtitle && (
                  <ThemedText
                    style={[styles.valueSubtitle, { color: selected.highlightSub ? "#FF00CE" : colors.textSecondary }]}
                  >
                    {selected.subtitle}
                  </ThemedText>
                )}
              </View>
              {!isEditing && idleAction && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    idleAction.onPress();
                  }}
                  hitSlop={8}
                  style={({ hovered }) => [styles.idleActionButton, hovered && { opacity: 0.7 }]}
                >
                  <MaterialCommunityIcons name={idleAction.icon} size={18} color={colors.textSecondary} />
                </Pressable>
              )}
              {selected.badge && <BadgePill text={selected.badge.text} tone={selected.badge.tone} />}
              {isEditing && !isSaving && (
                <MaterialCommunityIcons
                  name="chevron-down"
                  size={16}
                  color={colors.textSecondary}
                  style={{ opacity: 0.5 }}
                />
              )}
            </>
          ) : (
            <>
              <MaterialCommunityIcons name={icon} size={18} color={colors.placeholder} />
              <ThemedText style={[styles.placeholderText, { color: colors.placeholder }]}>{placeholder}</ThemedText>
              {isEditing && !isSaving && (
                <MaterialCommunityIcons
                  name="chevron-down"
                  size={16}
                  color={colors.textSecondary}
                  style={{ opacity: 0.5 }}
                />
              )}
            </>
          )}
        </Pressable>
      )}

      {/* Error text */}
      {hasError && (
        <View style={styles.errorRow}>
          <MaterialCommunityIcons name="alert-circle-outline" size={12} color={colors.red.text} />
          <ThemedText style={[styles.errorText, { color: colors.red.text }]}>{error}</ThemedText>
        </View>
      )}
    </View>
  );
}

// ── Styles ───────────────────────────────────────────────────

const styles = StyleSheet.create({
  field: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoTag: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: StyleSheet.hairlineWidth,
  },
  infoText: {
    fontSize: 10,
  },
  valueRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    minHeight: 40,
  },
  valueContent: {
    flex: 1,
    gap: 1,
  },
  valueName: {
    fontSize: 13,
    fontWeight: "600",
  },
  valueSubtitle: {
    fontSize: 11,
  },
  placeholderText: {
    flex: 1,
    fontSize: 13,
  },
  searchHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  searchHeaderText: {
    fontSize: 12,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    height: 40,
  },
  input: {
    flex: 1,
    fontSize: 13,
    // @ts-ignore web-only
    outlineStyle: "none",
    padding: 0,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  optionContent: {
    flex: 1,
    gap: 1,
  },
  optionName: {
    fontSize: 13,
  },
  optionSubtitle: {
    fontSize: 11,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: "600",
  },
  emptyRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  emptyText: {
    fontSize: 12,
  },
  idleActionButton: {
    padding: 4,
  },
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 2,
  },
  errorText: {
    fontSize: 11,
    fontWeight: "500",
  },
});
